import type { ICer } from "@bbj-types/candidate/cer";
import { CERStatus } from "@bbj-types/candidate/cerstatus";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import RestorePageOutlinedIcon from "@mui/icons-material/RestorePageOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { Authorizer } from "@styleguide";
import { type ChangeEvent, useRef } from "react";
import { classes } from "./styles";

interface ICerButtons {
  cer: Pick<ICer, "document" | "status" | "signature" | "structure">;
  onEdit?: () => void;
  handleDelete?: () => void;
  getPdf: (behaviour?: "download" | "display") => Promise<void>;
  onLoadDocument?: (data: ChangeEvent<HTMLInputElement>) => Promise<void>;
  transferToDraft?: () => void;
  loading: boolean;
  loadingUpload?: boolean;
}

export const CerButtons = ({
  cer,
  onEdit,
  handleDelete,
  getPdf,
  onLoadDocument,
  transferToDraft,
  loading: loadingCerDocument,
  loadingUpload,
}: ICerButtons) => {
  const { user } = useGlobalContext();
  const inputFileRef = useRef<HTMLInputElement>(null);
  switch (cer.status) {
    case CERStatus.DRAFT:
      return (
        <>
          {onEdit && (
            <Authorizer requiredPermissions={["candidate.cerV2.createOrUpdate"]}>
              <IconButton color="primary" onClick={onEdit}>
                <ModeEditOutlinedIcon className={classes.icon} />
              </IconButton>
            </Authorizer>
          )}
          {handleDelete && (
            <IconButton color="primary" onClick={handleDelete}>
              <DeleteOutlineOutlined className={classes.icon} />
            </IconButton>
          )}
        </>
      );
    case CERStatus.SIGNED:
    case CERStatus.SIGNED_PAPER:
      if (cer.document) {
        return loadingCerDocument ? (
          <CircularProgress size={24} sx={{ m: 1 }} />
        ) : (
          <>
            <IconButton color="primary" onClick={() => getPdf("download")}>
              <SaveAltIcon className={classes.icon} />
            </IconButton>
            {handleDelete && (
              <Authorizer requiredPermissions={["candidate.delete.cer.signed"]}>
                <IconButton color="primary" onClick={handleDelete}>
                  <DeleteOutlineOutlined className={classes.icon} />
                </IconButton>
              </Authorizer>
            )}
          </>
        );
      }
      return null;
    case CERStatus.TO_SIGN_PAPER:
      return loadingUpload ? (
        <CircularProgress size={24} sx={{ m: 1 }} />
      ) : (
        <>
          {onLoadDocument && (
            <IconButton color="primary" onClick={() => inputFileRef.current?.click()}>
              <Tooltip title="Importer le CE signé">
                <UploadOutlinedIcon className={classes.icon} />
              </Tooltip>
              <input
                accept=".pdf"
                type="file"
                style={{ display: "none" }}
                onChange={onLoadDocument}
                ref={inputFileRef}
                onClick={(event) => {
                  event.currentTarget.value = "";
                }}
              />
            </IconButton>
          )}
          {loadingCerDocument ? (
            <CircularProgress size={24} sx={{ m: 1 }} />
          ) : (
            <IconButton color="primary" onClick={() => getPdf("download")}>
              <Tooltip title="Imprimer le CE à signer">
                <LocalPrintshopOutlinedIcon className={classes.icon} />
              </Tooltip>
            </IconButton>
          )}
          {handleDelete && (
            <Authorizer requiredPermissions={["candidate.delete.cer.toSign"]}>
              <IconButton color="primary" onClick={handleDelete}>
                <DeleteOutlineOutlined className={classes.icon} />
              </IconButton>
            </Authorizer>
          )}
        </>
      );
    case CERStatus.TO_SIGN:
    case CERStatus.TO_SIGN_MANAGER:
    case CERStatus.TO_SIGN_CANDIDATE:
    case CERStatus.TO_VALIDATE_MANAGER:
      return (
        <>
          {transferToDraft && (
            <Authorizer requiredPermissions={["candidate.cer.transfer.draft"]}>
              <Tooltip title="Basculer le CE en brouillon et recommencer la procédure de signature">
                <IconButton color="primary" onClick={transferToDraft}>
                  <RestorePageOutlinedIcon className={classes.icon} />
                </IconButton>
              </Tooltip>
            </Authorizer>
          )}
          {handleDelete && (
            <Authorizer requiredPermissions={["candidate.delete.cer.toSign"]}>
              <IconButton color="primary" onClick={handleDelete}>
                <DeleteOutlineOutlined className={classes.icon} />
              </IconButton>
            </Authorizer>
          )}
        </>
      );
    case CERStatus.REFUSED_MANAGER:
      return user._id === cer.structure.author._id && onEdit ? (
        <Authorizer requiredPermissions={["candidate.cerV2.createOrUpdate"]}>
          <IconButton color="primary" onClick={onEdit}>
            <ModeEditOutlinedIcon className={classes.icon} />
          </IconButton>
        </Authorizer>
      ) : null;
  }
};
